import './Checkout.css';
import React from 'react';
import ReactGA, { set } from 'react-ga';
import { useFormik, FormikProvider, Form } from 'formik';
import * as Yup from 'yup';
import { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { ApiContext } from '../../Contexts/ApiContext';
import checkoutImg from './img/cc-img.png';
import checkoutImgForMobile from './img/cc-img-mobile.png';
import { CoreForm } from './CoreForm';
import { setPspCreds } from '../../Utils/setPspCreds';
import {
  setProductType,
  generateNoticeTextByMitigationType,
  convertFromNumberToText,
  convertTextsToStructuredFormat,
} from '../../Utils/creditCardUtils';
import { constants } from '../../Utils/constants';
import { mapCurrencyIntoHtmlCodeAndSymbol } from '../../Utils/currencyUtils';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import defaultTranslations from '../../locales/en.json';
import {
  convertFromNumberToPlannedCycle,
  convertPaymentMethodToText,
  paymentsDescriptionByPaymentArray,
} from '../../Utils/utils';

export const Checkout = (props) => {
  const M_APP_SRC_ARR = ['8', '9'];
  const {
    mitigationAction,
    token,
    dp,
    tla,
    atz,
    src,
    curr,
    pspId,
    offerName,
    productId,
    setupPeriod,
    businessTermsProp,
  } = props;

  const pspCredsObj = setPspCreds(pspId);
  const location = useLocation();
  const [isMounted, setIsMounted] = useState(false);
  const { verifyOrAuthorizePspToken, fetchLocalizedDataByLanguage, currencies } = useContext(ApiContext);
  const { currencySymbol } = mapCurrencyIntoHtmlCodeAndSymbol(curr, currencies);
  const [domState, setDomState] = useState('default'); //for card success/failure
  const [errMsg, setErrMsg] = useState('Error message from server');
  const [offerActivationNotice, setOfferActivationNotice] = useState('');
  const [businessTerms, setBusinessTerms] = useState('');
  const [translations, setTranslations] = useState(null);
  const { t } = useTranslation();

  const setupDisclaimer = constants.TEXTS.SETUP_DISCLAIMER;
  const fixedPaymentPlanIds = [constants.PRODUCT_IDS.FIXED_PAYMENT, constants.PRODUCT_IDS.CALCULATED_FIXED_PAYMENT];

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const lang = searchParams.get('lang') || 'en';
    fetchLanguages(lang);
  }, []);

  const fetchLanguages = async (language) => {
    try {
      const moduleType = 'client';
      const customerLanguageFiles = await fetchLocalizedDataByLanguage(language, moduleType);
      const { languageCodes, translations } = customerLanguageFiles;
      // i18n.addResourceBundle(languageCodes.defaultLanguageCode, 'translation', translations.defaultTranslations, true, true);
      i18n.addResourceBundle(
        languageCodes.customerLanguageCode,
        'translation',
        translations.customerTranslations,
        true,
        true,
      );
      i18n.changeLanguage(languageCodes.customerLanguageCode);
      setTranslations(translations.customerTranslations);
    } catch (error) {
      console.error('Error fetching languages:', error);
      i18n.changeLanguage('en');
      setTranslations(translations.defaultTranslations);
    }
  };

  const postMsgToHost = (respVerificationSuccessBool, respFailureReason, uniqueToken) => {
    if (respVerificationSuccessBool === true) {
      setDomState('cc_check');
      setDomState('verification_success');
      window.parent.postMessage(
        {
          message: 'iot_offer_select',
          unique_token: uniqueToken,
        },
        '*',
      );
    } else if (respVerificationSuccessBool === false) {
      setErrMsg(respFailureReason);
      setDomState('verification_failure');
      window.parent.postMessage(
        {
          message: 'iot_offer_rejected',
        },
        '*',
      );
    }
  };
  const formik = useFormik({
    initialValues: {
      card_holder_name: '',
      card_number: '',
      expiry_date: '',
      cvv: '',
    },
    onSubmit: async (values) => {
      setDomState('cc_check');
      const splitExpiryDate = values.expiry_date.split('20');
      const merchantId = pspCredsObj.mId;
      const reqId = parseInt(Math.random() * 1000000);
      const staticKey = pspCredsObj.sKey;
      try {
        const key = await window.keyCreation(
          merchantId,
          reqId,
          staticKey,
          values.card_number,
          splitExpiryDate[0],
          splitExpiryDate[1],
          values.cvv,
          values.card_holder_name,
        );
        const resp = await verifyOrAuthorizePspToken({
          urlToken: token,
          cardHolderName: values.card_holder_name,
          cardLastDigits: values.card_number.slice(-4),
          pspToken: JSON.parse(key),
        });
        if (resp?.data?.verifiedCardRespObj?.success === false) {
          setDomState('verification_failure');
          setErrMsg(resp.data.verifiedCardRespObj.reason);
        }
        if ([400, 401, 500].includes(resp?.response?.status)) {
          setDomState('verification_failure');
          setErrMsg(resp?.response?.data?.message);
        }
        if (resp?.status === 200) {
          const successBool = resp.data.verifiedCardRespObj.success ? resp.data.verifiedCardRespObj.success : false;
          setDomState('verification_success');
          postMsgToHost(successBool, resp?.data?.verifiedCardRespObj?.reason, resp?.data?.uniqueToken);
        }
      } catch (error) {
        console.error('Card submission failed:', error);
        setDomState('verification_failure');
        setErrMsg(error?.message || 'Something went wrong, please try again');
        return error;
      }
    },
    validationSchema: Yup.object({
      card_holder_name: Yup.string()
        .required('required')
        .matches(/^[a-zA-Z_ ]+$/, 'Cannot contain special characters or digits'),
      card_number: Yup.string()
        .min(13, 'At least 13 characters')
        .max(16, 'Maximum 16 characters')
        .required('required')
        .matches(/^[0-9]+$/, 'only digits'),
      expiry_date: Yup.string()
        .min(7, 'MM/YYYY')
        .max(7, 'MM/YYYY')
        .required('required')
        .matches(/^[0-9/]+$/, 'only digits'),
      cvv: Yup.string()
        .min(3, 'At least 3 digits')
        .max(4, 'Maximum 4 digits')
        .required('required')
        .matches(/^[0-9]+$/, 'only digits'),
    }),
  });

  useEffect(() => {
    if (isMounted) return;
    async function appendAndMountScripts(jQryScript, ppsKeyScript) {
      const jQueryScript = document.createElement('script');
      jQueryScript.src = jQryScript;
      jQueryScript.type = 'text/javascript';
      jQueryScript.id = 'jqry-script';
      document.body.appendChild(jQueryScript);
      const ppsKeyScriptCrdrx = document.createElement('script');
      ppsKeyScriptCrdrx.src = ppsKeyScript;
      ppsKeyScriptCrdrx.type = 'text/javascript';
      ppsKeyScriptCrdrx.id = 'jqry-script';
      document.body.appendChild(ppsKeyScriptCrdrx);
    }
    const jQueryScript = process.env.REACT_APP_JQ_SCRIPT;
    const pspKeyScript = pspCredsObj.script;
    appendAndMountScripts(jQueryScript, pspKeyScript).catch(console.error);

    ReactGA.event({
      category: 'NayaxCapital',
      action: 'CreditCard_Displayed',
    });
    setIsMounted(true);
  }, [isMounted, atz, dp, tla, src, mitigationAction, curr]);

  useEffect(() => {
    const { smallCurrencyUnitName } = mapCurrencyIntoHtmlCodeAndSymbol(curr, currencies);
    let selectedTranslations = defaultTranslations;
    if (translations) {
      selectedTranslations = translations;
    }
    const structuredTexts = convertTextsToStructuredFormat(selectedTranslations);

    // P9 (FIXED_PAYMENT_PLAN) has its own business terms, doesn't use mitigation-based notices
    if (parseInt(productId) === constants.PRODUCT_IDS.FIXED_PAYMENT_PLAN) {
      setOfferActivationNotice('');

      if ((businessTermsProp?.number_of_advance_payments ?? 0) > 0) {
        const numberOfAdvancePayments = businessTermsProp.number_of_advance_payments;

        let businessTermsRaw = structuredTexts.BUSINESS_TERMS.ADVANCE_PAYMENTS.map((line) =>
          line
            .replaceAll('{currencySymbol}', currencySymbol)
            .replace('{downPayment}', businessTermsProp.down_payment + businessTermsProp.advance_payments_total)
            .replaceAll('{numberOfAdvancePayments}', numberOfAdvancePayments)
            .replace('{paymentMethod}', convertPaymentMethodToText(businessTermsProp.default_payment_method_id))
        );
        // if (businessTermsProp.down_payment <= 0) {
        //   const plusIndex = businessTermsRaw[0].indexOf(' plus ');
        //   if (plusIndex !== -1) {
        //     businessTermsRaw[0] = businessTermsRaw[0].substring(plusIndex + ' plus '.length);
        //   }
        // }
        const businessTermsText = [...businessTermsRaw];
        // businessTermsText.push(`Total Remaining Balance: ${currencySymbol}${approvedPayment}.`);
        // businessTermsText.push('No interest.');
        // businessTermsText.push('No hidden fees.');
        setBusinessTerms(businessTermsText);
        return;
      }

      if (businessTermsProp?.pay_now === true) {
        const businessTermsText = structuredTexts.BUSINESS_TERMS.FULL_DOWN_PAYMENT.map((line) =>
          line.replace('{currencySymbol}', currencySymbol).replace('{advancePayment}', businessTermsProp.down_payment),
        );
        setBusinessTerms(businessTermsText);
        return;
      }

      const numberOfPayments = businessTermsProp.default_number_of_payments;
      const approvedPayment = parseFloat(tla);

      const businessTermsRaw = structuredTexts.BUSINESS_TERMS.FIXED_PAYMENT_PLAN.map((line) =>
        line
          .replaceAll('{currencySymbol}', currencySymbol)
          .replace('{advancePayment}', businessTermsProp.down_payment)
          .replace('{numOfInstallments}', numberOfPayments)
          .replace('{plannedCycle}', convertFromNumberToPlannedCycle(businessTermsProp.planned_cycle))
          .replace('{paymentMethod}', convertPaymentMethodToText(businessTermsProp.default_payment_method_id))
          .replace(
            '{calculatedInstallments}',
            paymentsDescriptionByPaymentArray(Array(numberOfPayments).fill(businessTermsProp.planned_amount), currencySymbol),
          )
          .replace('{totalPayment}', approvedPayment),
      );

      // Split second line into separate paragraphs for line breaks
      const businessTermsText = [];
      const LINE_BREAK_IDX = 1;
      const PARTS_LENGTH_FOR_BREAK = 2;
      businessTermsRaw.forEach((line, index) => {
        if (index === LINE_BREAK_IDX) {
          // Split on ". Total payment:" and ". No interest"
          const parts = line.split('. Total payment:');
          if (parts.length === PARTS_LENGTH_FOR_BREAK) {
            businessTermsText.push(parts[0] + '.');
            const remaining = 'Total payment:' + parts[1];
            const finalParts = remaining.split('. No interest');
            businessTermsText.push(finalParts[0] + '.');
            businessTermsText.push('No interest.');
            businessTermsText.push('No hidden fees.');
          } else {
            businessTermsText.push(line);
          }
        } else {
          businessTermsText.push(line);
        }
      });
      setBusinessTerms(businessTermsText);
      return;
    }

    const noticeText = generateNoticeTextByMitigationType(
      tla,
      dp,
      atz,
      mitigationAction,
      currencySymbol,
      structuredTexts,
    );
    setOfferActivationNotice(noticeText);

    const isPaymentProduct = setProductType(productId);
    if (isPaymentProduct) {
      setOfferActivationNotice(`${noticeText.substring(0, noticeText.length - 1)} ${structuredTexts.ENSURE}`);

      if (fixedPaymentPlanIds.includes(parseInt(productId))) {
        const currencyPattern = /[$£€]+/;
        const index = offerName.search(currencyPattern);
        const offerSubstring = offerName.substring(index);
        setBusinessTerms(
          structuredTexts.BUSINESS_TERMS.FIXED_AMOUNT.map((line) => line.replace('{offerSubstring}', offerSubstring)),
        );
      }

      if (parseInt(productId) === constants.PRODUCT_IDS.MCA_WITH_MIN) {
        const businessTermsText = structuredTexts.BUSINESS_TERMS.MIN_INSTALLMENT.map((line) =>
          line
            .replace('{currencySymbol}', currencySymbol)
            .replace('{share}', `${businessTermsProp.share}%`)
            .replace('{minInstallment}', businessTermsProp.min)
            .replace('{plannedCycle}', convertFromNumberToText(businessTermsProp.planned_cycle)),
        );
        setBusinessTerms(businessTermsText);
      }

      if (parseInt(productId) === constants.PRODUCT_IDS.MCA_PLUS_CPT_WITH_MIN) {
        const businessTermsText = structuredTexts.BUSINESS_TERMS.MIN_INSTALLMENT_PLUS_CPT.map((line) =>
          line
            .replace('{currencySymbol}', currencySymbol)
            .replace('{share}', `${businessTermsProp.share}%`)
            .replace('{minInstallment}', businessTermsProp.min)
            .replace('{plannedCycle}', convertFromNumberToText(businessTermsProp.planned_cycle))
            .replace('{cost_per_tx}', businessTermsProp.cost_per_tx * 100)
            .replace('{smallCurrencyUnitName}', smallCurrencyUnitName),
        );
        setBusinessTerms(businessTermsText);
      }
    }
  }, [translations, currencies, curr, currencySymbol, businessTermsProp, productId, offerName]);

  return (
    <FormikProvider value={formik}>
      <Form onChange={() => setDomState('default')}>
        <div className='whole-form'>
          <div className='flex'>
            <div className={M_APP_SRC_ARR.includes(src) ? 'd-none' : 'checkout-image-mobile'}>
              {/* <img src={checkoutImgForMobile} alt="" /> */}
            </div>
            <div className='checkout-section hurme-3-regular'>
              <div className='sticky-box'>
                <p
                  className={
                    M_APP_SRC_ARR.includes(src) ? 'd-none' : 'checkout-align-left hurme-3-semibold bigger-font'
                  }
                >
                  {t('thank_you_for_choosing_nayax_capital')}
                </p>
                <p className='checkout-align-left hurme-3-regular p-top'>{t('your_order_is_almost_ready')}</p>
                <p className='checkout-align-left hurme-3-regular p-top'>{offerActivationNotice}</p>
                {businessTerms !== '' && (
                  <>
                    {businessTerms.map((line, i) => {
                      return (
                        <p className='checkout-align-left hurme-3-regular' key={i}>
                          {line}
                        </p>
                      );
                    })}
                  </>
                )}
                {setupPeriod > 0 && parseInt(productId) !== constants.PRODUCT_IDS.FIXED_PAYMENT_PLAN && (
                  <>
                    {setupDisclaimer.map((line, i) => {
                      return (
                        <p className='checkout-align-left hurme-3-regular' key={i}>
                          {line.replace('{setupPeriod}', setupPeriod)}
                        </p>
                      );
                    })}
                  </>
                )}
              </div>
              <CoreForm
                formik={formik}
                domState={domState}
                mitigationAction={mitigationAction}
                errMsg={errMsg}
                operatingEntityId={businessTermsProp?.operating_entity_id}
              />
            </div>
            <div className='image-section'>
              <img src={checkoutImg} alt='' className='checkout-image' />
            </div>
          </div>
        </div>
      </Form>
    </FormikProvider>
  );
};
